import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfColor,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutImageGen } from '../layoutImage';
import { IItemCount } from '@educraft/base';
import { OptsImage, ImageTuple } from '../optsImage';
import { GUtil } from '../../pdf/gutil';
import { drawDitherLogo } from '../../pdf/ditherLogo';
import '../../pdf/draw/primitives';
import '../../pdf/draw/image';
import '../../pdf/draw/cut';
import '../../pdf/draw/string';
import { PDFPage } from 'pdf-lib';

/**
 * Dobble - Circular matching card game
 *
 * 7 portrait pages:
 * - Page 0: DitherLogo "DOBBLE" round backs (green)
 * - Pages 1-6: Card fronts with combinatorial image layout
 *   Each card has 6 images arranged in a circle, one center + 5 around edge
 *   Images on colored ellipse backgrounds, rotated around center
 *
 * Uses projective plane algorithm to generate cards where any two cards
 * share exactly one image.
 *
 * Migrated from C# EduFrame.Img10.Generators.Dobble
 */

function generateCards(itemsPerCard: number): number[][] {
  const cards: number[][] = [];
  const n = itemsPerCard - 1;
  // "n" must be prime

  for (let i = 0; i <= n; i++) {
    const card: number[] = [1];
    for (let j = 0; j < n; j++) {
      card.push(j + 1 + i * n + 1);
    }
    cards.push(card);
  }

  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      const card: number[] = [i + 2];
      for (let k = 0; k < n; k++) {
        card.push(n + 1 + n * k + ((i * k + j) % n) + 1);
      }
      cards.push(card);
    }
  }

  return cards;
}

export class Dobble implements ILayoutImageGen, IItemCount {
  countDefault(): number {
    return 8;
  }
  countMin(): number {
    return 10;
  }
  countMax(): number {
    return 31;
  }

  public async generate(pdf: PdfGen, opts: OptsImage): Promise<void> {
    const margin = PdfUnit.FromMillimeters(5);
    const cardSize = PdfUnit.FromMillimeters(85);
    const cardSizeReal = PdfUnit.FromMillimeters(80);
    const imgW = PdfUnit.FromMillimeters(23);
    const imgH = PdfUnit.FromMillimeters(23);
    const colors = [
      PdfColor.fromRgb(255, 0, 0), // Red
      PdfColor.fromRgb(0, 128, 0), // Green
      PdfColor.fromRgb(0, 0, 255), // Blue
      PdfColor.fromRgb(255, 255, 0), // Yellow
    ];
    const seed = opts.data.random.next(0, 2147483647);

    const count = 6;
    const maxCount = count * count - count + 1;
    const deck = generateCards(count);

    // Auto-fit grid
    const countX = Math.floor((PdfSize.A4.width - 2 * margin) / cardSize);
    const countY = Math.floor((PdfSize.A4.height - 2 * margin) / cardSize);
    const originX = (PdfSize.A4.width - countX * cardSize) / 2;
    const originY = (PdfSize.A4.height - countY * cardSize) / 2;

    // Shuffle all images with random colors
    const cardsData: { index: number; color: PdfColor | null }[] = [];

    if (opts.list.length >= maxCount) {
      for (let i = 0; i < opts.list.length; i++) {
        cardsData.push({ index: i, color: null });
      }
    } else {
      for (let i = 0; i < opts.list.length; i++) {
        for (const col of colors) {
          cardsData.push({ index: i, color: col });
        }
      }
    }

    // Fisher-Yates shuffle
    for (let i = cardsData.length - 1; i > 0; i--) {
      const j = opts.data.random.next(0, i + 1);
      [cardsData[i], cardsData[j]] = [cardsData[j], cardsData[i]];
    }

    function drawIm(page: PageGen, cardIndex: number, point: PdfPoint) {
      const {image} = opts.list[cardsData[cardIndex].index];
      const {color} = cardsData[cardIndex];
      const r = new PdfRect(point.x - imgW / 2, point.y - imgH / 2, imgW, imgH);

      if (color != null) {
        page.drawEllipse(null, new PdfBrush(color), r);
      }

      page.drawImageCenter(image, r);
    }

    // Page 0: DitherLogo round backs
    let pageIndex = 0;
    const pages: PageGen[] = [];

    // Card pages
    let cardIdx = 0;

    while (cardIdx < deck.length) {
      pages.push(pdf.addPage());
      pageIndex = pages.length - 1;

      for (let x = 0; x < countX; x++) {
        for (let y = 0; y < countY; y++) {
          const rect = new PdfRect(
            originX + x * cardSize,
            originY + y * cardSize,
            cardSizeReal,
            cardSizeReal
          );
          pages[pageIndex].drawEllipse(GUtil.cutPen, null, rect);

          if (cardIdx < deck.length) {
            // Shuffle card items
            const card = [...deck[cardIdx]];
            for (let i = card.length - 1; i > 0; i--) {
              const j = opts.data.random.next(0, i + 1);
              [card[i], card[j]] = [card[j], card[i]];
            }

            const first = card[0];
            const rest = card.slice(1);

            // Center image
            const cx = rect.x + rect.width / 2;
            const cy = rect.y + rect.height / 2;
            drawIm(pages[pageIndex], first, new PdfPoint(cx, cy));

            // Ring images
            for (let i = 0; i < rest.length; i++) {
              pages[pageIndex].save();

              pages[pageIndex].rotateAtTransform((360.0 / rest.length) * i, new PdfPoint(cx, cy));

              const edgeY = rect.y + rect.height - (imgH / 3) * 2;
              drawIm(pages[pageIndex], rest[i], new PdfPoint(cx, edgeY));

              pages[pageIndex].restore();
            }
          }

          cardIdx++;
        }
      }
    }

    pages.push(pdf.addPage());
    pageIndex++;

    for (let x = 0; x < countX; x++) {
      for (let y = 0; y < countY; y++) {
        const rect = new PdfRect(
          originX + x * cardSize,
          originY + y * cardSize,
          cardSizeReal,
          cardSizeReal
        );
        await drawDitherLogo(
          pages[pageIndex],
          'DOBBLE',
          PdfColor.fromRgb(0, 128, 0),
          rect,
          seed,
          true
        );
      }
    }
  }
}
